// One patient's contact log: every call, voicemail and email about money, newest
// first, and a form to add one. Contacts are never edited or deleted (A's service
// refuses both), so the form says what it records and there is no edit button.
//
// Statement emails appear here too, but only a send writes them — they are not in
// the form's kind list.

import React, { useEffect, useRef, useState } from "react";
import {
  CONTACT_KINDS,
  CONTACT_OUTCOMES,
  addContact,
  fetchContacts,
  formatDay,
  formatWhen,
  kindLabel,
  outcomeLabel,
} from "./api";
import "./Collections.css";

const NOTE_MAX = 2000;

function localToday() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export default function ContactLog({
  practiceId,
  patientId,
  readOnly = false,
  today = null,
  onLogged,
}) {
  const [contacts, setContacts] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [kind, setKind] = useState("call");
  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [nextContactOn, setNextContactOn] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setContacts(null);
    setLoadError(null);
    fetchContacts(practiceId, patientId)
      .then((rows) => {
        if (!cancelled) setContacts(rows);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, patientId]);

  const submit = async (e) => {
    e.preventDefault();
    if (inFlight.current || !kind) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const contact = await addContact(practiceId, patientId, {
        kind,
        outcome,
        note,
        nextContactOn,
      });
      if (contact) setContacts((rows) => [contact, ...(rows || [])]);
      setOutcome("");
      setNote("");
      setNextContactOn("");
      if (onLogged) onLogged(contact);
    } catch (err) {
      setError(err.message);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="coll-contact-log">
      <h3>Contact log</h3>

      {!readOnly && (
        <form className="coll-contact-form" onSubmit={submit}>
          <label className="coll-field">
            <span>What</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              disabled={busy}
            >
              {CONTACT_KINDS.map((k) => (
                <option key={k.key} value={k.key}>
                  {k.label}
                </option>
              ))}
            </select>
          </label>
          <label className="coll-field">
            <span>Outcome</span>
            <select
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              disabled={busy}
            >
              <option value="">—</option>
              {CONTACT_OUTCOMES.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="coll-field">
            <span>Follow up on</span>
            <input
              type="date"
              // The server checks against the practice's date, not the browser's.
              min={today || localToday()}
              value={nextContactOn}
              onChange={(e) => setNextContactOn(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="coll-field coll-field-wide">
            <span>Note</span>
            <textarea
              rows={2}
              maxLength={NOTE_MAX}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={busy}
              placeholder="e.g. spoke to patient, will pay Friday"
            />
          </label>
          <div className="coll-contact-submit">
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={busy}
            >
              {busy ? "Saving…" : "Log contact"}
            </button>
            <small className="coll-field-hint">
              Saved with your name and the time. Entries cannot be edited later.
            </small>
          </div>
          {error && (
            <div className="coll-message error coll-field-wide" role="alert">
              {error}
            </div>
          )}
        </form>
      )}

      {loadError && (
        <div className="coll-message error" role="alert">
          {loadError}
        </div>
      )}
      {!loadError && contacts === null && <p className="coll-muted">Loading…</p>}
      {contacts && contacts.length === 0 && (
        <p className="coll-muted">Nobody has contacted this patient yet.</p>
      )}
      {contacts && contacts.length > 0 && (
        <ul className="coll-contacts">
          {contacts.map((c) => (
            <li key={c.id}>
              <div className="coll-contact-head">
                <strong>{kindLabel(c.kind)}</strong>
                {c.outcome && (
                  <span className="coll-chip muted">
                    {outcomeLabel(c.outcome)}
                  </span>
                )}
                <span className="coll-muted">
                  {formatWhen(c.at)} · {c.byUser || "—"}
                </span>
                {c.nextContactOn && (
                  <span className="coll-muted">
                    follow up {formatDay(c.nextContactOn)}
                  </span>
                )}
              </div>
              {c.note && <p className="coll-contact-note">{c.note}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
